"use client";

import * as React from "react";
import ArthaLinkSmIcon from "../icons/arthalinkSmIcon";
import EditBrushIcon from "../icons/editBrushIcon";
import { ProfileAvatar } from "./profile-avatar";
import type { ShareOptionInstructions } from "./share-bottom-sheet";
import { ShareLinkRow } from "./share-link-row";
import arthaLinkFacebook from "../../assets/arthalinkfacebook.gif";
import arthaLinkInstagram from "../../assets/arthalinkInstagram.gif";
import arthaLinkX from "../../assets/arthalinkX.gif";
import arthaLinkYoutube from "../../assets/arthalinkYoutube.gif";

export interface ShareInstructionsContentProps {
  profileName: string;
  profileHandle: string;
  profileAvatar?: string;
  shareLink: string;
  icon?: React.ReactNode;
  instructions: ShareOptionInstructions;
  onBack: () => void;
  onCopyLink?: () => void;
  onEditSettings?: () => void;
  onShowToast?: (message: string) => void;
}

export function ShareInstructionsContent({
  profileName,
  profileHandle,
  profileAvatar,
  shareLink,
  icon,
  instructions,
  onBack,
  onCopyLink,
  onEditSettings,
  onShowToast,
}: ShareInstructionsContentProps) {
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  type GifAsset = string | { src: string };
  const platformGif = React.useMemo<GifAsset | null>(() => {
    const title = instructions.title?.toLowerCase() ?? "";
    if (title.includes("instagram")) return arthaLinkInstagram;
    if (title.includes("twitter") || title.includes("x")) return arthaLinkX;
    if (title.includes("facebook")) return arthaLinkFacebook;
    if (title.includes("youtube")) return arthaLinkYoutube;
    return null;
  }, [instructions.title]);
  const platformGifSrc =
    typeof platformGif === "string" ? platformGif : platformGif?.src ?? null;

  const handleActionClick = async () => {
    if (instructions.onActionClick) {
      // Show toast immediately
      const platformName = instructions.title || 'platform';
      onShowToast?.(`Link Copied and Redirecting to ${platformName}...`);

      // Copy link to clipboard (without showing toast to avoid conflict)
      if (onCopyLink) {
        // Copy the link value directly to clipboard
        try {
          await navigator.clipboard.writeText(instructions.linkValue || shareLink);
        } catch {
          // clipboard write failed silently
        }
      }


      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        instructions.onActionClick();
      } catch {
        onShowToast?.('Redirect failed. Please try again.');
      } finally {
        setIsRedirecting(false);
      }
    }
  };

  return (
    <div className="flex flex-col gap-4 overflow-y-auto pb-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to share"
          className="flex h-8 w-8 items-center justify-center rounded-md text-[#344054]"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M11.625 3.75L6.375 9L11.625 14.25"
              stroke="#344054"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {/* <p className="text-base font-semibold text-[#344054] capitalize">{instructions.title}</p> */}
      </div>

      {platformGifSrc ? (
        <div className="relative rounded-lg border border-black overflow-hidden bg-white">
          <img src={platformGifSrc} alt={`${instructions.title} instructions`} className="h-auto w-full" />
        </div>
      ) : (
        <div
          className="relative rounded-lg border border-black px-4 pb-6 pt-6"
          style={{
            background: instructions.gradient ?? "linear-gradient(90deg, #F6DDFF 0%, #F6DDFF 100%)",
          }}
        >
          {onEditSettings && (
            <button
              type="button"
              onClick={onEditSettings}
              className="absolute right-3 top-3 rounded-md border border-black bg-white p-1.5 text-[#344054] shadow-[0_2px_0_rgba(0,0,0,0.08)]"
              aria-label="Edit share settings"
            >
              <EditBrushIcon />
            </button>
          )}
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-[82px] w-[82px] items-center justify-center rounded-full border-4 border-white bg-white/40">
              <ProfileAvatar
                src={profileAvatar}
                alt={profileName}
                className="h-[74px] w-[74px] rounded-full"
                fallbackClassName="bg-white"
                logoClassName="h-[58%] w-[58%]"
              />
            </div>
            <div className="flex flex-col items-center gap-1">
              <p className="text-[20px] font-black uppercase leading-none text-black font-phudu">{profileName}</p>
              <div className="flex items-center gap-1 text-sm text-[#58616F]">
                <ArthaLinkSmIcon className="h-3.5 w-3.5" />
                <span>{profileHandle}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {instructions.steps?.length > 0 && (
        <ol className="list-decimal space-y-2 pl-5 text-sm text-[#475467]">
          {instructions.steps.map((step, index) => (
            <li key={index} className="*:leading-6">
              {step}
            </li>
          ))}
        </ol>
      )}

      {instructions.helperText && <p className="text-xs text-[#98A2B3]">{instructions.helperText}</p>}

      <ShareLinkRow link={instructions.linkValue ?? shareLink} onCopy={onCopyLink} />

      {instructions.actionLabel && (
        <button
          type="button"
          className="flex items-center justify-between rounded-lg border border-black bg-white px-3 py-2 text-left disabled:opacity-50"
          onClick={handleActionClick}
          disabled={isRedirecting}
        >
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-md  bg-white">
              {icon}
            </div>
            <span className="text-sm text-[#667085]">
              {instructions.actionLabel}
            </span>
          </div>
          <span className="text-[#98A2B3]">
            {instructions.actionIcon ?? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6 4L10 8L6 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
        </button>
      )}
    </div>
  );
}
